import { useEffect, useMemo, useState } from 'react';
import { promotionsAPI } from '@/lib/api';

/**
 * Site-wide top banner that surfaces the soonest-expiring active promotion
 * and ticks down a UTC-anchored countdown. Hides itself the moment every
 * promo expires (no admin action required).
 *
 * Architecture decisions:
 * - We poll /api/promotions/active once at mount + every 60s so an admin
 *   killing a promo via /admin/promotions takes effect within a minute.
 * - The countdown ticker runs in the browser and is anchored to the server's
 *   `now` timestamp from the API response, so users with a wrong system clock
 *   still see the correct remaining time.
 * - A small grace check on every tick re-fetches once the timer hits 0 to
 *   confirm with the server before permanently hiding (handles browsers that
 *   left the tab open across the cutoff).
 */
export default function PromoBanner() {
  const [data, setData] = useState(null);     // {promotions, now, soonest_expiry}
  const [skewMs, setSkewMs] = useState(0);    // server_now - browser_now
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const res = await promotionsAPI.active();
      setData(res);
      if (res?.now) {
        setSkewMs(new Date(res.now).getTime() - Date.now());
      }
    } catch {
      // Silently ignore; banner just stays hidden.
    }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 60_000);
    const ticker = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(ticker); };
  }, []);

  const remainingMs = useMemo(() => {
    if (!data?.soonest_expiry) return 0;
    const exp = new Date(data.soonest_expiry).getTime();
    const serverNow = Date.now() + skewMs;
    return Math.max(0, exp - serverNow);
  }, [data, skewMs, tick]);

  // Re-fetch once when timer hits zero to confirm with the server.
  useEffect(() => {
    if (data?.soonest_expiry && remainingMs === 0) {
      const id = setTimeout(load, 1500);
      return () => clearTimeout(id);
    }
  }, [remainingMs, data?.soonest_expiry]);

  if (!data?.promotions?.length || remainingMs <= 0) return null;

  // Compose the banner message: prefer the eval-fee (sitewide) promo's
  // banner_label since that's what the user sees as "site sale".
  const headline = data.promotions.find(p => p.banner_label)?.banner_label
    || data.promotions[0]?.name
    || 'Limited-time sale';
  const tags = data.promotions.map(p => p.promo_tag).filter(Boolean);
  const { d, h, m, s } = breakdown(remainingMs);

  return (
    <div
      className="w-full bg-gradient-to-r from-[#00ff66]/10 via-[#ff6b1a]/10 to-[#00ff66]/10 border-b border-[#00ff66]/30"
      data-testid="promo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm">
        <span className="font-mono tracking-wider text-[#00ff66]">
          {tags[0] || 'LIMITED-TIME SALE'}
        </span>
        <span className="text-zinc-300">{headline}:</span>
        <span className="font-mono text-base text-white tabular-nums" data-testid="promo-countdown">
          {d > 0 && <><span className="text-[#00ff66]">{d}</span><span className="text-zinc-500 mx-1">d</span></>}
          <span className="text-[#00ff66]">{pad(h)}</span><span className="text-zinc-500 mx-1">h</span>
          <span className="text-[#00ff66]">{pad(m)}</span><span className="text-zinc-500 mx-1">m</span>
          <span className="text-[#00ff66]">{pad(s)}</span><span className="text-zinc-500 ml-1">s</span>
        </span>
        {tags.length > 1 && (
          <span className="text-[11px] tracking-widest font-mono text-[#ff8c42] hidden sm:inline">
            + {tags.slice(1).join(' · ')}
          </span>
        )}
      </div>
    </div>
  );
}

function breakdown(ms) {
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { d, h, m, s };
}

function pad(n) { return String(n).padStart(2, '0'); }
